import React from "react";
import type { DeliveryFailureView } from "../api/types";
import { Banner } from "../ds";
import { useT } from "../i18n";
import { Icon, fmtStamp } from "../lib";

/**
 * What the relay told us after it accepted the send (#711).
 *
 * This sits next to the draft the operator is looking at, because the whole point of the
 * feature is that "sent" stopped being the last word: the send succeeded, and the message
 * still never arrived. A separate log nobody opens would not change what the operator
 * believes.
 *
 * It states what happened and what it means, and offers no button. Deliberately: there is no
 * safe automatic move here. Resending is what the system must never do on its own, and the
 * right next step — phoning the customer, asking a colleague for the correct address — is not
 * something the cockpit can perform.
 */

type Copy = { title: string; body: string; tone: "danger" | "warning" };

function copyFor(
  failure: DeliveryFailureView,
  t: ReturnType<typeof useT>,
): Copy | null {
  switch (failure.kind) {
    case "hard_bounce":
      return {
        tone: "danger",
        title: t("deliveryFailure.hardBounce.title"),
        body: t("deliveryFailure.hardBounce.body"),
      };
    case "complaint":
      return {
        tone: "danger",
        title: t("deliveryFailure.complaint.title"),
        body: t("deliveryFailure.complaint.body"),
      };
    case "soft_bounce":
      return {
        tone: "warning",
        title: t("deliveryFailure.softBounce.title"),
        body: t("deliveryFailure.softBounce.body", {
          count: String(failure.soft_bounce_count),
          threshold: String(failure.soft_bounce_threshold),
        }),
      };
    default:
      // A kind this build does not know about. Saying nothing beats inventing an explanation
      // of a delivery problem for an operator who then acts on it.
      return null;
  }
}

export function DeliveryFailureBanner({ failure }: { failure: DeliveryFailureView }) {
  const t = useT();
  const copy = copyFor(failure, t);
  if (!copy) return null;

  return (
    <Banner
      tone={copy.tone}
      title={copy.title}
      icon={<Icon name="alert" size={18} />}
      data-testid="delivery-failure-banner"
    >
      <p>{copy.body}</p>
      <dl className="delivery-failure__meta">
        {failure.recipient && (
          <>
            <dt>{t("deliveryFailure.recipient")}</dt>
            <dd>{failure.recipient}</dd>
          </>
        )}
        {failure.occurred_at && (
          <>
            <dt>{t("deliveryFailure.occurredAt")}</dt>
            <dd>{fmtStamp(failure.occurred_at)}</dd>
          </>
        )}
        {failure.diagnostic && (
          <>
            <dt>{t("deliveryFailure.diagnostic")}</dt>
            {/* Third-party text, rendered as text. React escapes it; it is never markup. */}
            <dd>{failure.diagnostic}</dd>
          </>
        )}
      </dl>
    </Banner>
  );
}
